import React, { useEffect, useReducer, useRef, useState } from 'react'
import { useAccount, useReadContracts } from 'wagmi'
import { useTranslation } from 'react-i18next'
import { useBalance } from 'wagmi'
import { useLocation, useSearchParams } from 'react-router-dom'
import ReactGA from 'react-ga4'

import App from '../App'
import { WALLET_ACCOUNT_STATUS } from '../util/walletConnect.helpers'
import {
  mainReducer,
  mainInitialState,
  MAIN_PROFILE_DISPATCH
} from '../store/reducers/main'
import {
  changeMainState,
  resetMainState,
  updateData
} from '../store/actions/main'
import {
  contractAddress,
  getChain,
  wagmiContractConfig
} from '../components/walletConnect/wagmiConfig'
import Loading from '../components/shared/loading/Loading'
import {
  getWalletAPI,
  getAccountWalletAPI,
  postReferralCodeAPI,
  postAppReferralCodeAPI
} from '../util/api.helpers'
import {
  getAppProvider,
  getAppUserId,
  getReferralCode,
  setAppProvider,
  setAppUserId,
  setReferralCode
} from '../util/local.helpers'
import { POLL_ACCOUNT_WALLET_INFO_API_TIME } from '../constants/app'
import {
  getContractConfig,
  getTimeNow,
  isSaleEnded,
  scrollToTop
} from '../util/app.helpers'
import Notifications from '../Notifications'

const chainId = getChain().id

const AppContainer = () => {
  const { t } = useTranslation()

  const location = useLocation()
  useEffect(() => {
    scrollToTop()
    const gaMeasurementId = process.env.REACT_APP_GTM_ID
    if (gaMeasurementId) {
      ReactGA.send({
        hitType: 'pageview',
        page: location.pathname + location.search
      })
    }
  }, [location])

  const [mainProfile, mainDispatch] = useReducer(mainReducer, mainInitialState)
  const { address, status } = useAccount()

  const balanceResponse = useBalance({
    address: contractAddress,
    chainId
  })

  const isWalletConnected =
    status === WALLET_ACCOUNT_STATUS.CONNECTED && address

  useEffect(() => {
    if (isWalletConnected) {
      getWalletAPI(mainDispatch, address)
      getAccountWalletAPI(mainDispatch, address, true)
    }
    if (status === 'disconnected') {
      mainDispatch(resetMainState())
    }
  }, [status, address])

  const accountWalletInfoInterval = useRef(null)

  const [saleTime, setSaleTime] = useState(getTimeNow())

  const endTime = getContractConfig().end

  useEffect(() => {
    if (isWalletConnected && !isSaleEnded() && saleTime <= endTime) {
      accountWalletInfoInterval.current = setInterval(() => {
        setSaleTime(getTimeNow())
        fetchAccountWalletInfo()
      }, POLL_ACCOUNT_WALLET_INFO_API_TIME)
    }
    return () => {
      clearAccountWalletInfoInterval()
    }
  }, [isWalletConnected])

  useEffect(() => {
    if (saleTime > endTime) {
      clearAccountWalletInfoInterval()
    }
  }, [saleTime])

  const clearAccountWalletInfoInterval = () => {
    clearInterval(accountWalletInfoInterval.current)
  }

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const code = searchParams.get('code')
    if (code) {
      setReferralCode(code)
    }
    const appProvider = searchParams.get('app_provider')
    const appUserId = searchParams.get('app_user_id')
    if (appProvider && appUserId) {
      setAppProvider(appProvider)
      setAppUserId(appUserId)
    }
    setSearchParams({})
  }, [searchParams])

  useEffect(() => {
    const code = getReferralCode()
    if (isWalletConnected && !mainProfile.referred && code) {
      postReferralCodeAPI(mainDispatch, address, code)
    }
  }, [isWalletConnected, mainProfile.referred])

  useEffect(() => {
    const appProvider = getAppProvider()
    const appUserId = getAppUserId()
    if (isWalletConnected && appProvider && appUserId) {
      postAppReferralCodeAPI(mainDispatch, address, appProvider, appUserId)
    }
  }, [isWalletConnected])

  useEffect(() => {
    if (balanceResponse.data) {
      dispatchAction(
        MAIN_PROFILE_DISPATCH.TOTAL_RAISED,
        balanceResponse.data.formatted
      )
    }
  }, [balanceResponse.data])

  const contractResponse = useReadContracts({
    contracts: [
      ...(isWalletConnected
        ? [
            {
              ...wagmiContractConfig,
              functionName: 'amounts',
              args: [address]
            }
          ]
        : [])
    ]
  })

  useEffect(() => {
    if (contractResponse?.data?.length) {
      mainDispatch(updateData(contractResponse.data))
    }
  }, [contractResponse.data, mainProfile.resultUserDeposit])

  const handleClickRefresh = () => {
    fetchAccountWalletInfo()
    balanceResponse.refetch()
    contractResponse.refetch()
  }

  const fetchAccountWalletInfo = () => {
    getAccountWalletAPI(mainDispatch, address)
  }

  const dispatchAction = (attr, value) => {
    mainDispatch(changeMainState(attr, value))
  }
  return (
    <>
      {contractResponse.isLoading ||
      ['connecting', 'reconnecting'].includes(status) ? (
        <Loading />
      ) : (
        <>
          <App
            t={t}
            onClickRefresh={handleClickRefresh}
            mainDispatch={mainDispatch}
            mainProfile={mainProfile}
            saleEnded={saleTime > endTime}
          />
          {mainProfile.hardRefresh && isWalletConnected && (
            <Notifications mainProfile={mainProfile} t={t} />
          )}
        </>
      )}
    </>
  )
}

export default AppContainer
